import { z } from "zod";
import { buildMemoryVersionId, memoryApplicabilitySchema, memoryAuthoritySchema, memoryKindSchema, memoryScopeTypeSchema, memoryStatusSchema, memoryTypeSchema, memoryVerificationSchema, memoryVersionIdSchema, } from "./memories.js";
import { intentionalMemoryTraceRefSchema, memoryTraceScopeSchema, } from "./memory-trace.js";
export const MAX_INTENTIONAL_MEMORY_PACKET_ITEMS = 25;
export const MAX_INTENTIONAL_MEMORY_PACKET_CONTENT_CHARS = 4_012;
const packetIdentifierSchema = z.string().trim().min(1).max(240);
const canonicalTextSchema = (maximum) => z.string()
    .min(1)
    .max(maximum)
    .refine((value) => value === value.trim(), {
    message: "Packet text must already be trimmed",
});
const packetTimestampSchema = z.number().int().nonnegative().safe();
const sourceSessionUrlSchema = z.string().url().max(2_048).refine((value) => {
    try {
        const parsed = new URL(value);
        return (parsed.protocol === "http:" || parsed.protocol === "https:") &&
            !parsed.username && !parsed.password;
    }
    catch {
        return false;
    }
}, {
    message: "Source session URLs must be credential-free HTTP(S) URLs",
});
/**
 * One immutable intentional-memory version, materialised for a single digest.
 * Nullable fields are deliberately explicit so packet identity is not affected
 * by different serializers omitting different pieces of metadata.
 */
export const intentionalMemoryDigestPacketItemSchema = z.object({
    ordinal: z.number().int().min(0).max(MAX_INTENTIONAL_MEMORY_PACKET_ITEMS - 1),
    memoryRef: intentionalMemoryTraceRefSchema,
    versionId: memoryVersionIdSchema,
    scopeType: memoryScopeTypeSchema,
    scopeId: canonicalTextSchema(500),
    memoryKey: canonicalTextSchema(200),
    status: memoryStatusSchema,
    memoryType: memoryTypeSchema,
    kind: memoryKindSchema,
    content: canonicalTextSchema(MAX_INTENTIONAL_MEMORY_PACKET_CONTENT_CHARS),
    source: canonicalTextSchema(500),
    authority: memoryAuthoritySchema,
    applicability: memoryApplicabilitySchema,
    validFrom: packetTimestampSchema.nullable(),
    validTo: packetTimestampSchema.nullable(),
    observedAt: packetTimestampSchema,
    ingestedAt: packetTimestampSchema,
    verification: memoryVerificationSchema,
    sourceSessionId: canonicalTextSchema(500).nullable(),
    sourceSessionUrl: sourceSessionUrlSchema.nullable(),
    sourceRunId: canonicalTextSchema(500).nullable(),
    createdAt: packetTimestampSchema,
    updatedAt: packetTimestampSchema,
}).strict().superRefine((item, context) => {
    const expectedVersionId = buildMemoryVersionId(item.memoryRef.memoryId, item.memoryRef.version);
    if (item.versionId !== expectedVersionId) {
        context.addIssue({
            code: "custom",
            path: ["versionId"],
            message: "versionId must identify the exact intentional memory reference",
        });
    }
    if (item.validTo !== null && item.validFrom !== null && item.validTo <= item.validFrom) {
        context.addIssue({
            code: "custom",
            path: ["validTo"],
            message: "validTo must be greater than validFrom",
        });
    }
    if (item.sourceSessionUrl !== null && item.sourceSessionId === null) {
        context.addIssue({
            code: "custom",
            path: ["sourceSessionUrl"],
            message: "A source session URL requires a source session id",
        });
    }
});
/**
 * Server-owned, ordered v1 input to the slow-changing intentional-memory lane.
 */
export const intentionalMemoryDigestPacketSchema = z.object({
    schemaVersion: z.literal(1),
    packetId: packetIdentifierSchema,
    scope: memoryTraceScopeSchema,
    items: z.array(intentionalMemoryDigestPacketItemSchema)
        .max(MAX_INTENTIONAL_MEMORY_PACKET_ITEMS),
}).strict().superRefine((packet, context) => {
    const seen = new Set();
    for (const [index, item] of packet.items.entries()) {
        if (item.ordinal !== index) {
            context.addIssue({
                code: "custom",
                path: ["items", index, "ordinal"],
                message: "Packet item ordinals must match their array order",
            });
        }
        const refKey = `${item.memoryRef.memoryId}:${item.memoryRef.version}`;
        if (seen.has(refKey)) {
            context.addIssue({
                code: "custom",
                path: ["items", index, "memoryRef"],
                message: "A memory version may appear only once in a digest packet",
            });
        }
        seen.add(refKey);
        if (item.scopeType === "organisation" && packet.scope.organisationId === null) {
            context.addIssue({
                code: "custom",
                path: ["items", index, "scopeType"],
                message: "Organisation memory requires an authoritative organisation scope",
            });
        }
        if (item.scopeType === "channel" && packet.scope.channelId === null) {
            context.addIssue({
                code: "custom",
                path: ["items", index, "scopeType"],
                message: "Channel memory requires an authoritative channel scope",
            });
        }
    }
});
/**
 * Runtime-to-control-plane revalidation immediately before a surfaced memory
 * is allowed to influence an external calculation tool. This closes the
 * retrieval/application race when a memory is retracted or superseded while a
 * run is already in flight.
 */
export const workerMemoryApplicationValidationRequestSchema = z.object({
    runId: packetIdentifierSchema,
    tenantSlug: packetIdentifierSchema,
    traceId: packetIdentifierSchema,
    versionIds: z.array(memoryVersionIdSchema)
        .min(1)
        .max(MAX_INTENTIONAL_MEMORY_PACKET_ITEMS)
        .refine((values) => new Set(values).size === values.length, {
        message: "Memory version ids must be unique",
    }),
}).strict();
export const workerMemoryApplicationValidationResponseSchema = z.object({
    schemaVersion: z.literal(1),
    valid: z.literal(true),
    versionIds: z.array(memoryVersionIdSchema)
        .min(1)
        .max(MAX_INTENTIONAL_MEMORY_PACKET_ITEMS),
}).strict();
function packetItemProvenance(item) {
    const fields = [
        `memory ${item.memoryRef.memoryId}@${item.memoryRef.version}`,
        `immutable version ID ${item.versionId}`,
        `scope ${item.scopeType}:${item.scopeId}`,
        `status ${item.status}`,
        `type ${item.memoryType}`,
        `authority ${item.authority}`,
        `applicability ${JSON.stringify(item.applicability)}`,
        `verification ${item.verification.status}`,
        `observed ${new Date(item.observedAt).toISOString()}`,
        `ingested ${new Date(item.ingestedAt).toISOString()}`,
        ...(item.validFrom !== null
            ? [`valid from ${new Date(item.validFrom).toISOString()}`]
            : []),
        ...(item.validTo !== null
            ? [`valid to ${new Date(item.validTo).toISOString()}`]
            : []),
        `created ${new Date(item.createdAt).toISOString()}`,
        `updated ${new Date(item.updatedAt).toISOString()}`,
    ];
    if (item.sourceSessionId) {
        fields.push(`source session ${item.sourceSessionId}`);
        if (item.sourceSessionUrl)
            fields.push(`session link ${item.sourceSessionUrl}`);
    }
    if (item.sourceRunId)
        fields.push(`source run ${item.sourceRunId}`);
    return fields.join(", ");
}
export function renderIntentionalMemoryDigestPacketItem(rawItem) {
    const item = intentionalMemoryDigestPacketItemSchema.parse(rawItem);
    return `[${item.scopeType}] ${item.memoryKey} (${item.kind}, ${item.source}; ${packetItemProvenance(item)}): ${item.content}`;
}
export function renderIntentionalMemoryDigestPacketContent(rawPacket) {
    const packet = intentionalMemoryDigestPacketSchema.parse(rawPacket);
    return packet.items.map(renderIntentionalMemoryDigestPacketItem).join("\n");
}
